import { UserAvatar } from '@/shared/components/custom/avatars/UserAvatar';
import { getUserColor } from '@/core/lib/get-user-color';
import { TimelineTaskBar } from './TimelineTaskBar';
import {
  computeBarPosition,
  TIMELINE_PERSON_COLUMN_WIDTH_PX,
  TIMELINE_ROW_HEIGHT_PX,
} from '../../utils/timeline-geometry';
import type { TimelineTaskRowModel } from '../../types';

interface TimelineTaskRowProps {
  task: TimelineTaskRowModel;
  columnCount: number;
  columnWidthPx: number;
  windowStart: Date;
  windowEnd: Date;
  onTaskClick: (taskId: string) => void;
}

/** One task row when the Timeline is grouped by status/priority/group — avatar+name of the task's own assignee, then its single bar. */
export function TimelineTaskRow({
  task,
  columnCount,
  columnWidthPx,
  windowStart,
  windowEnd,
  onTaskClick,
}: TimelineTaskRowProps) {
  const totalWidthPx = columnCount * columnWidthPx;
  const position = computeBarPosition(
    task.startDate,
    task.endDate,
    windowStart,
    windowEnd,
    totalWidthPx,
  );

  return (
    <div
      data-testid="tasks-timeline-task-row"
      className="flex items-stretch border-b border-border"
      style={{ height: TIMELINE_ROW_HEIGHT_PX }}
    >
      <div
        className="sticky left-0 z-10 flex shrink-0 items-center gap-2 border-r border-border bg-background pl-3.5"
        style={{ width: TIMELINE_PERSON_COLUMN_WIDTH_PX }}
      >
        {task.assignee && (
          <UserAvatar
            name={task.assignee.name}
            color={getUserColor(task.assignee.id)}
            src={task.assignee.avatarUrl}
            size="sm"
          />
        )}
        <p className="truncate text-[13px] font-medium text-foreground">
          {task.assignee?.name ?? '—'}
        </p>
      </div>

      <div className="relative shrink-0" style={{ width: totalWidthPx }}>
        {position && (
          <TimelineTaskBar
            task={task}
            leftPx={position.leftPx}
            widthPx={position.widthPx}
            onClick={onTaskClick}
          />
        )}
      </div>
    </div>
  );
}
